import classNames from "./Select.module.css";
import * as Popover from "@radix-ui/react-popover";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import Button, { Color } from "./Button";

export type SelectItem = {
  value?: string | number | boolean;
  label: ReactNode;
};

const Select = (props: {
  selected: SelectItem["value"];
  onChange: (value: SelectItem["value"]) => void;
  items: SelectItem[];
  align?: "start" | "end" | "center";
  color?: Color;
  backgroundColor?: Color;
  borderColor?: Color;
  triggerClassName?: string;
  contentClassName?: string;
  defaultLabel?: string;
}) => {
  const [open, setOpen] = useState(false);

  const selectedItem = useMemo(() => {
    return props.items.find((item) => item.value === props.selected);
  }, [props.items, props.selected]);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>();
  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [props.selected]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        ref={triggerRef}
        className={`${classNames.Trigger} ${props.triggerClassName ?? ""}`}
        asChild
      >
        <Button
          color={props.color || "gray"}
          borderColor={props.borderColor || "dark-gray"}
          backgroundColor={props.backgroundColor || "dark-gray"}
        >
          <span>
            {selectedItem?.label ?? props.defaultLabel ?? "Select..."}
          </span>
          {!open ? <IoChevronDown /> : <IoChevronUp />}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={`${classNames.Content} ${props.contentClassName ?? ""}`}
          style={{ minWidth: triggerWidth }}
          side="bottom"
          align={props.align || "start"}
          sideOffset={8}
        >
          {props.items.map((item) => (
            <button
              key={String(item.value)}
              type="button"
              onClick={() => {
                setOpen(false);
                props.onChange(item.value);
              }}
              aria-selected={props.selected === item.value}
            >
              {item.label}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default Select;
